import { useId } from "react"
import type { DamageZone } from "../types"
import { BODY_PATH, GLASS_PATH, MIRRORS, SEAMS, WHEELS, ZONE_RECT } from "./car-plan"
import { ZONE_ORDER } from "./shot-plan"

// The top-down car, drawn once for both the zone picker and the stand map.
// Pure presentation: highlighted panels fill with the signal green; anything
// interactive is layered on top by the caller.

export function CarDrawing({
  marked,
  tone = "paper",
  markedOpacity = 1,
  tint = null,
}: {
  marked: readonly DamageZone[]
  // A panel under the mouse (zone picker hover) — subtle, clipped to the body.
  tint?: DamageZone | null
  // "paper" = light screens; "dark" = on the camera's black.
  tone?: "paper" | "dark"
  markedOpacity?: number
}) {
  // useId() output can contain characters that break url(#…) references.
  const clipId = `car-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`
  const dark = tone === "dark"
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <path d={BODY_PATH} />
        </clipPath>
      </defs>
      {WHEELS.map((w, i) => (
        <rect
          key={i}
          x={w.x}
          y={w.y}
          width={w.w}
          height={w.h}
          rx={5}
          fill={dark ? "#3f3f46" : "#27272a"}
        />
      ))}
      {MIRRORS.map((d, i) => (
        <path key={i} d={d} fill={dark ? "#52525b" : "#d4d4d8"} />
      ))}
      <path d={BODY_PATH} fill={dark ? "#18181b" : "#ffffff"} />
      <path d={GLASS_PATH} fill={dark ? "#ffffff" : "#0c2018"} fillOpacity={dark ? 0.07 : 0.06} />
      <g clipPath={`url(#${clipId})`}>
        {tint && !marked.includes(tint) && (
          <rect
            x={ZONE_RECT[tint].x}
            y={ZONE_RECT[tint].y}
            width={ZONE_RECT[tint].w}
            height={ZONE_RECT[tint].h}
            fill={dark ? "#ffffff" : "#0c2018"}
            fillOpacity={0.07}
          />
        )}
        {ZONE_ORDER.filter((z) => marked.includes(z)).map((z) => {
          const r = ZONE_RECT[z]
          return (
            <rect
              key={z}
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              fill={dark ? "var(--color-signal-on-dark)" : "var(--color-signal)"}
              fillOpacity={markedOpacity}
            />
          )
        })}
      </g>
      {SEAMS.map(([x1, y1, x2, y2], i) => (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={dark ? "#ffffff" : "#0c2018"}
          strokeOpacity={dark ? 0.16 : 0.14}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <path
        d={BODY_PATH}
        fill="none"
        stroke={dark ? "#ffffff" : "#0c2018"}
        strokeOpacity={dark ? 0.35 : 0.42}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  )
}
